import { supabase, isSupabaseConfigured } from '@/lib/supabase'

export interface Badge {
  id: string
  name: string
  emoji: string
  description: string
  category: 'missions' | 'words' | 'streak' | 'mastery'
  requirement: number
  // Progress function returns current progress toward this badge
  getProgress: (stats: ChildStats) => number
}

export interface ChildStats {
  missionsCompleted: number
  totalWordsWritten: number
  currentStreak: number
  skillsMastered: number
}

export interface BadgeProgress {
  badge: Badge
  progress: number
  isEarned: boolean
}

// Badge definitions - ordered by difficulty within each category
export const BADGES: Badge[] = [
  // Mission badges
  {
    id: 'first-mission',
    name: 'First Steps',
    emoji: '🌱',
    description: 'Complete your first mission',
    category: 'missions',
    requirement: 1,
    getProgress: (s) => s.missionsCompleted,
  },
  {
    id: 'mission-5',
    name: 'Mission Master',
    emoji: '🎯',
    description: 'Complete 5 missions',
    category: 'missions',
    requirement: 5,
    getProgress: (s) => s.missionsCompleted,
  },
  {
    id: 'mission-10',
    name: 'Unstoppable',
    emoji: '🚀',
    description: 'Complete 10 missions',
    category: 'missions',
    requirement: 10,
    getProgress: (s) => s.missionsCompleted,
  },
  {
    id: 'mission-25',
    name: 'Legend',
    emoji: '👑',
    description: 'Complete 25 missions',
    category: 'missions',
    requirement: 25,
    getProgress: (s) => s.missionsCompleted,
  },

  // Word count badges
  {
    id: 'words-100',
    name: 'Wordsmith',
    emoji: '✏️',
    description: 'Write 100 words',
    category: 'words',
    requirement: 100,
    getProgress: (s) => s.totalWordsWritten,
  },
  {
    id: 'words-500',
    name: 'Storyteller',
    emoji: '📖',
    description: 'Write 500 words',
    category: 'words',
    requirement: 500,
    getProgress: (s) => s.totalWordsWritten,
  },
  {
    id: 'words-1000',
    name: 'Author',
    emoji: '📚',
    description: 'Write 1,000 words',
    category: 'words',
    requirement: 1000,
    getProgress: (s) => s.totalWordsWritten,
  },
  {
    id: 'words-5000',
    name: 'Novelist',
    emoji: '🏆',
    description: 'Write 5,000 words',
    category: 'words',
    requirement: 5000,
    getProgress: (s) => s.totalWordsWritten,
  },

  // Streak badges
  {
    id: 'streak-3',
    name: 'On a Roll',
    emoji: '🔥',
    description: '3 day writing streak',
    category: 'streak',
    requirement: 3,
    getProgress: (s) => s.currentStreak,
  },
  {
    id: 'streak-7',
    name: 'Week Warrior',
    emoji: '⚡',
    description: '7 day writing streak',
    category: 'streak',
    requirement: 7,
    getProgress: (s) => s.currentStreak,
  },
  {
    id: 'streak-14',
    name: 'Dedicated',
    emoji: '💪',
    description: '14 day writing streak',
    category: 'streak',
    requirement: 14,
    getProgress: (s) => s.currentStreak,
  },

  // Mastery badges
  {
    id: 'skill-1',
    name: 'Skill Unlocked',
    emoji: '⭐',
    description: 'Master your first skill',
    category: 'mastery',
    requirement: 1,
    getProgress: (s) => s.skillsMastered,
  },
  {
    id: 'skill-5',
    name: 'Growing Expert',
    emoji: '🌟',
    description: 'Master 5 skills',
    category: 'mastery',
    requirement: 5,
    getProgress: (s) => s.skillsMastered,
  },
  {
    id: 'skill-10',
    name: 'Writing Pro',
    emoji: '💫',
    description: 'Master 10 skills',
    category: 'mastery',
    requirement: 10,
    getProgress: (s) => s.skillsMastered,
  },
]

/**
 * Calculates badge progress for a child
 */
export function calculateBadgeProgress(stats: ChildStats): {
  earned: BadgeProgress[]
  nextUp: BadgeProgress | null
  all: BadgeProgress[]
} {
  const all: BadgeProgress[] = BADGES.map((badge) => {
    const progress = badge.getProgress(stats)
    return {
      badge,
      progress,
      isEarned: progress >= badge.requirement,
    }
  })

  const earned = all.filter((b) => b.isEarned)
  const unearned = all.filter((b) => !b.isEarned)

  // Find the badge closest to being earned (highest % progress)
  let nextUp: BadgeProgress | null = null
  if (unearned.length > 0) {
    nextUp = unearned.reduce((best, current) => {
      const bestPct = best.progress / best.badge.requirement
      const currentPct = current.progress / current.badge.requirement
      return currentPct > bestPct ? current : best
    })
  }

  return { earned, nextUp, all }
}

/**
 * Loads stats needed for badge calculation
 */
export async function loadChildStats(childId: string): Promise<ChildStats | null> {
  if (!isSupabaseConfigured()) return null

  const [missionsResult, childResult, skillsResult] = await Promise.all([
    supabase
      .from('mission_completions')
      .select('*', { count: 'exact', head: true })
      .eq('child_id', childId),
    supabase
      .from('children')
      .select('total_words_written, current_streak_days')
      .eq('id', childId)
      .single(),
    supabase
      .from('child_skill_progress')
      .select('*', { count: 'exact', head: true })
      .eq('child_id', childId)
      .eq('status', 'mastered'),
  ])

  if (!childResult.data) return null

  return {
    missionsCompleted: missionsResult.count || 0,
    totalWordsWritten: childResult.data.total_words_written || 0,
    currentStreak: childResult.data.current_streak_days || 0,
    skillsMastered: skillsResult.count || 0,
  }
}

/**
 * Finds badges that were newly earned by comparing before/after stats
 */
export function findNewlyEarnedBadges(beforeStats: ChildStats, afterStats: ChildStats): Badge[] {
  const newBadges: Badge[] = []

  for (const badge of BADGES) {
    const beforeProgress = badge.getProgress(beforeStats)
    const afterProgress = badge.getProgress(afterStats)

    // Badge was not earned before but is earned now
    if (beforeProgress < badge.requirement && afterProgress >= badge.requirement) {
      newBadges.push(badge)
    }
  }

  return newBadges
}
